import '../i18n/autoTranslate';
import React, { useEffect, useMemo, useState } from "react";
import Confetti from "react-confetti";
import {
  Award,
  BarChart3,
  CheckCircle2,
  Loader2,
  Sparkles,
  Target,
  TrendingUp,
  X,
} from "lucide-react";

function useWindowSize() {
  const [size, setSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1200,
    height: typeof window !== "undefined" ? window.innerHeight : 800,
  });

  useEffect(() => {
    const onResize = () => {
      setSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return size;
}

function asArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

export default function FinalResultsModal({
  open,
  loading = false,
  data = null,
  error = "",
  onClose,
  onGoToDashboard,
  onReviewAnswers,
}) {
  const { width, height } = useWindowSize();
  const [confettiActive, setConfettiActive] = useState(false);

  useEffect(() => {
    if (!open || loading || error || !data) return;
    setConfettiActive(true);
    const timer = setTimeout(() => setConfettiActive(false), 8500);
    return () => clearTimeout(timer);
  }, [open, loading, error, data]);

  const score = Number(data?.score ?? data?.final_score ?? 0);
  const seniority = data?.seniority || data?.level || "Candidate";
  const summary =
    data?.summary ||
    data?.overall_assessment ||
    "Session completed. Review your strengths and improvement areas.";

  const strengths = useMemo(
    () => asArray(data?.strengths).slice(0, 4),
    [data]
  );

  const weaknesses = useMemo(
    () => asArray(data?.weaknesses || data?.areas_for_improvement).slice(0, 4),
    [data]
  );

  const radarData = useMemo(
    () => asArray(data?.radarData || data?.radar_data).slice(0, 5),
    [data]
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto bg-[#050509] text-white">
      {confettiActive && (
        <Confetti
          width={width}
          height={height}
          recycle={false}
          numberOfPieces={420}
          gravity={0.18}
        />
      )}

      <div className="pointer-events-none fixed inset-0">
        <div className="absolute left-1/2 top-[-20%] h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-indigo-500/20 blur-[120px]" />
        <div className="absolute bottom-[-25%] left-[-10%] h-[520px] w-[520px] rounded-full bg-fuchsia-500/10 blur-[120px]" />
        <div className="absolute right-[-10%] top-[25%] h-[560px] w-[560px] rounded-full bg-cyan-500/10 blur-[120px]" />
        <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:24px_24px] opacity-[0.12]" />
      </div>

      <div className="relative mx-auto flex min-h-screen w-full max-w-7xl flex-col px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 backdrop-blur-xl">
            <Sparkles className="h-4 w-4 text-indigo-300" />
            <span className="text-sm font-semibold text-white/80">
              Final AI Interview Results
            </span>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] text-white/70 transition hover:bg-white/[0.08] hover:text-white"
            aria-label="Close results"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex flex-1 items-center justify-center">
            <div className="w-full max-w-lg rounded-[2rem] border border-white/10 bg-white/[0.04] p-10 text-center shadow-[0_30px_100px_-50px_rgba(99,102,241,0.8)] backdrop-blur-2xl">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-3xl border border-indigo-400/20 bg-indigo-500/10">
                <Loader2 className="h-10 w-10 animate-spin text-indigo-300" />
              </div>
              <h2 className="text-3xl font-bold">Analyzing your session…</h2>
              <p className="mt-3 text-white/60">
                The AI is reviewing your answers, code quality, communication,
                and improvement areas.
              </p>
            </div>
          </div>
        ) : error ? (
          <div className="flex flex-1 items-center justify-center">
            <div className="w-full max-w-xl rounded-[2rem] border border-red-400/20 bg-red-500/10 p-10 text-center backdrop-blur-2xl">
              <h2 className="text-3xl font-bold text-red-100">
                Final analysis failed
              </h2>
              <p className="mt-3 text-red-100/75">{error}</p>
              <button
                type="button"
                onClick={onClose}
                className="mt-8 rounded-2xl bg-white px-6 py-3 text-sm font-bold text-black transition hover:bg-white/90"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <section className="grid gap-6 lg:grid-cols-[1fr_420px]">
              <div className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 shadow-[0_30px_100px_-50px_rgba(99,102,241,0.8)] backdrop-blur-2xl sm:p-8">
                <div className="flex flex-col gap-8 md:flex-row md:items-center">
                  <div className="relative mx-auto flex h-52 w-52 shrink-0 items-center justify-center rounded-full md:mx-0">
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: `conic-gradient(rgba(99,102,241,.95) ${Math.max(
                          0,
                          Math.min(100, score)
                        )}%, rgba(255,255,255,.10) 0)`,
                      }}
                    />
                    <div className="absolute inset-[12px] rounded-full border border-white/10 bg-[#07070d]" />
                    <div className="relative text-center">
                      <div className="text-6xl font-black tracking-tight">
                        {Math.round(score)}
                      </div>
                      <div className="mt-1 text-sm font-semibold uppercase tracking-[0.25em] text-white/45">
                        Score
                      </div>
                    </div>
                  </div>

                  <div className="min-w-0 flex-1 text-center md:text-left">
                    <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-500/10 px-3 py-1 text-sm font-semibold text-emerald-200">
                      <CheckCircle2 className="h-4 w-4" />
                      Interview completed
                    </div>
                    <h1 className="text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">
                      Great work.
                      <span className="block bg-gradient-to-r from-indigo-300 via-fuchsia-200 to-cyan-200 bg-clip-text text-transparent">
                        {seniority} Level
                      </span>
                    </h1>
                    <p className="mt-5 max-w-3xl text-base leading-8 text-white/65 sm:text-lg">
                      {summary}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 backdrop-blur-2xl sm:p-8">
                <div className="mb-6 flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.05]">
                    <Award className="h-6 w-6 text-amber-200" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Performance Snapshot</h2>
                    <p className="text-sm text-white/50">AI-generated scoring</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {radarData.length ? (
                    radarData.map((item) => {
                      const label = item.subject || item.name || "Metric";
                      const value = Number(item.A ?? item.value ?? 0);
                      return (
                        <div key={label}>
                          <div className="mb-2 flex items-center justify-between text-sm">
                            <span className="text-white/70">{label}</span>
                            <span className="font-bold">{Math.round(value)}%</span>
                          </div>
                          <div className="h-2.5 overflow-hidden rounded-full bg-white/10">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-fuchsia-500"
                              style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
                            />
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm text-white/55">
                      No detailed metrics were returned, but your final score
                      and summary are ready.
                    </p>
                  )}
                </div>
              </div>
            </section>

            <section className="mt-6 grid gap-6 lg:grid-cols-2">
              <div className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 backdrop-blur-2xl sm:p-8">
                <div className="mb-6 flex items-center gap-3">
                  <TrendingUp className="h-6 w-6 text-emerald-300" />
                  <h2 className="text-2xl font-bold">Strengths</h2>
                </div>
                <div className="space-y-3">
                  {(strengths.length ? strengths : ["You completed the full interview flow."]).map(
                    (item, index) => (
                      <div
                        key={`${item}-${index}`}
                        className="rounded-2xl border border-emerald-400/10 bg-emerald-500/10 p-4 text-emerald-50/85"
                      >
                        {item}
                      </div>
                    )
                  )}
                </div>
              </div>

              <div className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 backdrop-blur-2xl sm:p-8">
                <div className="mb-6 flex items-center gap-3">
                  <Target className="h-6 w-6 text-indigo-300" />
                  <h2 className="text-2xl font-bold">Next Improvements</h2>
                </div>
                <div className="space-y-3">
                  {(weaknesses.length
                    ? weaknesses
                    : ["Practice adding more concrete examples, trade-offs, and measurable outcomes."]
                  ).map((item, index) => (
                    <div
                      key={`${item}-${index}`}
                      className="rounded-2xl border border-indigo-400/10 bg-indigo-500/10 p-4 text-indigo-50/85"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <div className="sticky bottom-0 mt-8 border-t border-white/10 bg-[#050509]/80 py-4 backdrop-blur-xl">
              <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={onReviewAnswers}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.05] px-6 py-3 text-sm font-bold text-white/75 transition hover:bg-white/[0.09] hover:text-white"
                >
                  <BarChart3 className="h-4 w-4" />
                  Review Answers
                </button>
                <button
                  type="button"
                  onClick={onGoToDashboard}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 to-fuchsia-600 px-6 py-3 text-sm font-bold text-white shadow-[0_20px_60px_-30px_rgba(99,102,241,0.9)] transition hover:from-indigo-500 hover:to-fuchsia-500"
                >
                  Go to Dashboard
                  <Sparkles className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
